import React, { useState, useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  TextField,
  Grid,
  Alert,
  Typography,
  Autocomplete,
} from '@mui/material';
import {
  useRouter,
  PageHeader,
  PageContent,
  FormBottomBar,
  FormErrorDialog,
  useFormError,
  useNotification,
  useModuleAccess,
} from '@viilutalous/common';
import { useEmployeePayroll, employeeLinksAPI } from '@viilutalous/salaries';
import { employeesAPI, departmentsAPI, usersAPI } from './employeesAPI';

export default function EmployeeForm({ tenantId, setBreadcrumbTitle }) {
  const { navigate, params } = useRouter();
  const { t } = useTranslation();
  const { showSuccess } = useNotification();
  const { error: formError, dialogOpen: errorDialogOpen, handleApiError, showError, closeDialog } = useFormError();
  const { id } = params;
  const isNew = id === 'new' || !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const { isEnabled: payrollEnabled } = useModuleAccess('salaries');
  const [savedEmployee, setSavedEmployee] = useState(null);
  // Name and henkilötunnus are locked once the employee is linked to Salaxy (BIZ-741).
  const [payrollStatus, setPayrollStatus] = useState(null);
  const payrollLinked = !!payrollStatus?.linked;

  const loadPayrollStatus = useCallback(() => {
    if (isNew || !payrollEnabled || !tenantId) return;
    employeeLinksAPI
      .get(tenantId, id)
      .then((res) => setPayrollStatus(res.data))
      .catch(() => setPayrollStatus(null));
  }, [isNew, payrollEnabled, tenantId, id]);

  useEffect(() => {
    loadPayrollStatus();
  }, [loadPayrollStatus]);

  const payroll = useEmployeePayroll(tenantId, { onChanged: loadPayrollStatus });

  const { register, handleSubmit, control, setValue, trigger, watch, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      work_email: '',
      work_phone: '',
      work_mobile: '',
      job_title: '',
      department: null,
      manager: null,
      user: null,
      private_email: '',
      private_phone: '',
      birthday: '',
      gender: '',
      nationality: '',
      identification_no: '',
      bank_account: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      private_street: '',
      private_city: '',
      private_zip: '',
      private_country: '',
    },
  });

  const nameValue = watch('name');

  // Dropdown options
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  const [pendingUserId, setPendingUserId] = useState(null);

  useEffect(() => {
    return () => {
      if (setBreadcrumbTitle) {
        setBreadcrumbTitle(null);
      }
    };
  }, [setBreadcrumbTitle]);

  useEffect(() => {
    if (tenantId) {
      fetchDepartments();
      fetchEmployees();
      fetchUsers();
      if (!isNew) {
        fetchEmployee();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, id]);

  useEffect(() => {
    if (pendingUserId && users.length > 0) {
      const matchedUser = users.find(u => u.id === pendingUserId);
      if (matchedUser) {
        setValue('user', matchedUser);
      }
      setPendingUserId(null);
    }
  }, [pendingUserId, users, setValue]);

  const fetchDepartments = async () => {
    try {
      const response = await departmentsAPI.getAll(tenantId);
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await employeesAPI.getAll(tenantId);
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getByTenant(tenantId);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchEmployee = async () => {
    try {
      setLoading(true);
      const response = await employeesAPI.getOne(tenantId, id);
      const emp = response.data;
      setSavedEmployee(emp);

      setValue('name', emp.name || '');
      setValue('work_email', emp.work_email || '');
      setValue('work_phone', emp.work_phone || '');
      setValue('work_mobile', emp.work_mobile || '');
      setValue('job_title', emp.job_title || '');
      setValue('private_email', emp.private_email || '');
      setValue('private_phone', emp.private_phone || '');
      setValue('birthday', emp.birthday || '');
      setValue('gender', emp.gender || '');
      setValue('nationality', emp.nationality || '');
      setValue('identification_no', emp.identification_no || '');
      setValue('bank_account', emp.bank_account || '');
      setValue('emergency_contact_name', emp.emergency_contact_name || '');
      setValue('emergency_contact_phone', emp.emergency_contact_phone || '');
      setValue('private_street', emp.private_street || '');
      setValue('private_city', emp.private_city || '');
      setValue('private_zip', emp.private_zip || '');
      setValue('private_country', emp.private_country || '');

      if (emp.department_id) {
        setValue('department', { id: emp.department_id, name: emp.department_name || '' });
      }
      if (emp.manager_id) {
        setValue('manager', { id: emp.manager_id, name: emp.manager_name || '' });
      }
      if (emp.user_id) {
        setPendingUserId(emp.user_id);
      }

      if (setBreadcrumbTitle) {
        setBreadcrumbTitle(emp.name);
      }
    } catch (error) {
      console.error('Error fetching employee:', error);
      handleApiError(error, t);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setSaving(true);
      const payload = {
        name: data.name.trim(),
        work_email: data.work_email || null,
        work_phone: data.work_phone || null,
        work_mobile: data.work_mobile || null,
        department_id: data.department?.id || null,
        job_title: data.job_title || null,
        manager_id: data.manager?.id || null,
        user_id: data.user?.id || null,
        private_email: data.private_email || null,
        private_phone: data.private_phone || null,
        birthday: data.birthday || null,
        gender: data.gender || null,
        nationality: data.nationality || null,
        identification_no: data.identification_no || null,
        bank_account: data.bank_account || null,
        emergency_contact_name: data.emergency_contact_name || null,
        emergency_contact_phone: data.emergency_contact_phone || null,
        private_street: data.private_street || null,
        private_city: data.private_city || null,
        private_zip: data.private_zip || null,
        private_country: data.private_country || null,
      };
      if (payrollLinked) {
        delete payload.name;
        delete payload.identification_no;
      }

      if (isNew) {
        await employeesAPI.create(tenantId, payload);
        showSuccess(t('employees.created'));
      } else {
        await employeesAPI.update(tenantId, id, payload);
        showSuccess(t('employees.updated'));
      }
      navigate('/employees');
    } catch (error) {
      console.error('Error saving employee:', error);
      if (error.response?.data?.detail?.code === 'locked_by_payroll') {
        showError(t('salaries.error.locked_by_payroll'));
      } else {
        handleApiError(error, t);
      }
    } finally {
      setSaving(false);
    }
  };

  const onSubmitError = (formErrors) => {
    const errorMessages = [];
    Object.entries(formErrors).forEach(([, error]) => {
      if (error?.message) {
        errorMessages.push(error.message);
      }
    });
    if (errorMessages.length > 0) {
      showError(errorMessages.join('\n'));
    }
  };

  const handleSaveClick = async (e) => {
    if (e) e.preventDefault();
    const isValid = await trigger();
    if (!isValid) {
      onSubmitError(errors);
      return;
    }
    handleSubmit(onSubmit, onSubmitError)(e);
  };

  const handleCancel = () => {
    navigate('/employees');
  };

  if (!tenantId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">{t('employees.select_tenant')}</Alert>
      </Box>
    );
  }

  return (
    <>
      <PageHeader />

      <PageContent>
        {payrollLinked && payrollStatus.last_error_code && (
          <Alert severity="warning" sx={{ mb: 3, maxWidth: 900 }}>
            {t('salaries.employees.sync_failed', { error: t(`salaries.error.${payrollStatus.last_error_code}`) })}
          </Alert>
        )}

        {/* Work Information */}
        <Paper sx={{ p: 3, mb: 3, maxWidth: 900 }}>
          <Typography variant="h6" gutterBottom>
            {t('employees.section.work')}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('employees.name')}
                {...register('name', { required: t('employees.error.name_required') })}
                error={!!errors.name}
                helperText={errors.name?.message || (payrollLinked ? t('salaries.employees.locked_hint') : undefined)}
                required
                disabled={loading || payrollLinked}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('employees.job_title')}
                {...register('job_title')}
                disabled={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('employees.work_email')}
                {...register('work_email')}
                error={!!errors.work_email}
                helperText={errors.work_email?.message}
                disabled={loading}
                type="email"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('employees.work_phone')}
                {...register('work_phone')}
                disabled={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('employees.work_mobile')}
                {...register('work_mobile')}
                disabled={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="department"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={departments.filter((dept) => dept.id !== id)}
                    getOptionLabel={(option) => option.name || ''}
                    value={field.value}
                    onChange={(e, val) => field.onChange(val)}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderInput={(params) => (
                      <TextField {...params} label={t('employees.department')} size="small" />
                    )}
                    disabled={loading}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="manager"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={employees.filter((emp) => emp.id !== id)}
                    getOptionLabel={(option) => option.name || ''}
                    value={field.value}
                    onChange={(e, val) => field.onChange(val)}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderInput={(params) => (
                      <TextField {...params} label={t('employees.manager')} size="small" />
                    )}
                    disabled={loading}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="user"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={users}
                    getOptionLabel={(option) => {
                      if (!option) return '';
                      const name = [option.firstname, option.lastname].filter(Boolean).join(' ');
                      return name ? `${name} (${option.email})` : option.email || '';
                    }}
                    value={field.value}
                    onChange={(e, val) => field.onChange(val)}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderInput={(params) => (
                      <TextField {...params} label={t('employees.user')} size="small" />
                    )}
                    disabled={loading}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Personal Information */}
        <Paper sx={{ p: 3, mb: 3, maxWidth: 900 }}>
          <Typography variant="h6" gutterBottom>
            {t('employees.section.personal')}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('employees.private_email')}
                {...register('private_email')}
                error={!!errors.private_email}
                helperText={errors.private_email?.message}
                disabled={loading}
                type="email"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('employees.private_phone')}
                {...register('private_phone')}
                disabled={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('employees.birthday')}
                {...register('birthday')}
                disabled={loading}
                type="date"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('employees.gender')}
                {...register('gender')}
                disabled={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('employees.nationality')}
                {...register('nationality')}
                disabled={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('employees.identification_no')}
                {...register('identification_no')}
                helperText={payrollLinked ? t('salaries.employees.locked_hint') : undefined}
                disabled={loading || payrollLinked}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('employees.bank_account')}
                {...register('bank_account')}
                disabled={loading}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Emergency Contact */}
        <Paper sx={{ p: 3, mb: 3, maxWidth: 900 }}>
          <Typography variant="h6" gutterBottom>
            {t('employees.section.emergency')}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('employees.emergency_contact_name')}
                {...register('emergency_contact_name')}
                disabled={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('employees.emergency_contact_phone')}
                {...register('emergency_contact_phone')}
                disabled={loading}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Private Address */}
        <Paper sx={{ p: 3, mb: 3, maxWidth: 900 }}>
          <Typography variant="h6" gutterBottom>
            {t('employees.section.address')}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                fullWidth
                label={t('employees.private_street')}
                {...register('private_street')}
                disabled={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label={t('employees.private_zip')}
                {...register('private_zip')}
                disabled={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label={t('employees.private_city')}
                {...register('private_city')}
                disabled={loading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label={t('employees.private_country')}
                {...register('private_country')}
                disabled={loading}
              />
            </Grid>
          </Grid>
        </Paper>
      </PageContent>

      <FormBottomBar
        onSave={handleSaveClick}
        onCancel={handleCancel}
        saving={saving}
        disabled={!nameValue?.trim()}
        actions={payrollEnabled && !isNew ? payroll.actionsFor(savedEmployee, payrollStatus) : []}
      />

      {payroll.dialogs}

      <FormErrorDialog
        open={errorDialogOpen}
        onClose={closeDialog}
        message={formError?.message}
      />
    </>
  );
}
